import { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  VisibilityOffOutlined,
  OpenInNewOutlined,
  FullscreenOutlined,
  Add,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from "@store/hooks";
import { setPaperEditorMode } from "@store/slices/viewSlice";
import type { PaperEditorMode } from "@store/slices/viewSlice";
import {
  selectCurrentPaper,
  selectIsCurrentUserWorkspaceOwner,
  selectPapers,
} from "@store/selectors";
import { selectPaper } from "@store/thunks/workspaceThunks";
import { CreatePaperModal } from "@features/annogram/resources/components/CreatePaperModal";
import { TOUR } from "@features/onboarding/tourTargets";

/**
 * PaperEditorButton - Button with dropdown for paper switching + editor
 * visibility mode. Mirrors the ResourceSelector dropdowns structurally:
 *   - Current paper header
 *   - Switch list
 *   - Mode controls (paper-editor-specific)
 *   - + New Paper (evaluator-only)
 */
export default function PaperEditorButton() {
  const dispatch = useAppDispatch();
  const paperEditorMode = useAppSelector((state) => state.view.paperEditorMode);
  const workspaceId = useAppSelector(
    (state) => state.workspace.workspace_id
  );
  const papers = useAppSelector(selectPapers);
  const currentPaper = useAppSelector(selectCurrentPaper);
  // Paper creation is an owner capability (the API gates POST paper on
  // workspace ownership, not tier).
  const isOwner = useAppSelector(selectIsCurrentUserWorkspaceOwner);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [createPaperOpen, setCreatePaperOpen] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleModeChange = (mode: PaperEditorMode) => {
    dispatch(setPaperEditorMode(mode));
    handleClose();
  };

  const handleSelectPaper = (paperId: string) => {
    if (paperId !== currentPaper?.paper_id) {
      dispatch(selectPaper(paperId));
      // If the editor's currently hidden, surface the chosen paper as a
      // floating window so the switch has a visible effect.
      if (paperEditorMode === "hidden") {
        dispatch(setPaperEditorMode("modal"));
      }
    }
    handleClose();
  };

  const getModeIcon = () => {
    switch (paperEditorMode) {
      case 'hidden':
        return <VisibilityOffOutlined />;
      case 'modal':
        return <OpenInNewOutlined />;
      case 'fullscreen':
        return <FullscreenOutlined />;
    }
  };

  const getModeLabel = () => {
    switch (paperEditorMode) {
      case 'hidden':
        return 'Paper (Hidden)';
      case 'modal':
        return 'Paper (Floating)';
      case 'fullscreen':
        return 'Paper (Fullscreen)';
    }
  };

  const hasPapers = papers.length > 0;
  const isVisible = paperEditorMode !== 'hidden';
  // Owners can always open the menu so they can create the first paper.
  const buttonDisabled = !hasPapers && !isOwner;
  const tooltipTitle = hasPapers
    ? "Toggle Paper Editor (Ctrl+3)"
    : isOwner
    ? "No papers yet — open menu to create one"
    : "No papers available";

  const otherPapers = currentPaper
    ? papers.filter((p) => p.paper_id !== currentPaper.paper_id)
    : papers;

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <span>
          <Button
            data-tour={TOUR.paperEditorButton}
            onClick={handleClick}
            startIcon={getModeIcon()}
            disabled={buttonDisabled}
            sx={{
              // Per-mode "active" green (see theme.ts contrast notes).
              color: isVisible ? 'success.main' : 'inherit',
            }}
            aria-label="paper editor options"
          >
            {getModeLabel()}
          </Button>
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: { minWidth: 280, maxHeight: 500 },
          },
        }}
      >
        {currentPaper && [
          <Box
            key="current-paper"
            sx={{ px: 2, py: 1.5, bgcolor: "action.hover" }}
          >
            <Typography variant="caption" color="text.secondary">
              Current Paper
            </Typography>
            <Typography variant="subtitle2" sx={{ mt: 0.5 }} noWrap>
              {currentPaper.paper_name || "Untitled Paper"}
            </Typography>
          </Box>,
          <Divider key="header-divider" />,
        ]}

        {hasPapers && (
          <MenuItem disabled>
            <Typography variant="caption">Switch Paper</Typography>
          </MenuItem>
        )}

        {otherPapers.map((paper) => (
          <MenuItem
            key={paper.paper_id}
            onClick={() => handleSelectPaper(paper.paper_id)}
          >
            <ListItemText
              primary={paper.paper_name || "Untitled Paper"}
            />
          </MenuItem>
        ))}

        {hasPapers && otherPapers.length === 0 && (
          <MenuItem disabled>
            <Typography variant="caption" color="text.secondary">
              No other papers in this workspace
            </Typography>
          </MenuItem>
        )}

        {hasPapers && <Divider />}

        <MenuItem
          onClick={() => handleModeChange('hidden')}
          selected={paperEditorMode === 'hidden'}
          disabled={!hasPapers}
        >
          <ListItemIcon>
            <VisibilityOffOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Hidden"
            secondary="Close paper editor"
          />
        </MenuItem>
        <MenuItem
          onClick={() => handleModeChange('modal')}
          selected={paperEditorMode === 'modal'}
          disabled={!hasPapers}
        >
          <ListItemIcon>
            <OpenInNewOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Floating Window"
            secondary="Side-by-side editing"
          />
        </MenuItem>
        <MenuItem
          onClick={() => handleModeChange('fullscreen')}
          selected={paperEditorMode === 'fullscreen'}
          disabled={!hasPapers}
        >
          <ListItemIcon>
            <FullscreenOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Fullscreen"
            secondary="Focused writing mode"
          />
        </MenuItem>

        {isOwner && [
          <Divider key="divider" />,
          <MenuItem
            key="new-paper"
            onClick={() => {
              handleClose();
              setCreatePaperOpen(true);
            }}
          >
            <ListItemIcon>
              <Add fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="New Paper" />
          </MenuItem>,
        ]}
      </Menu>

      {workspaceId && (
        <CreatePaperModal
          open={createPaperOpen}
          onClose={() => setCreatePaperOpen(false)}
          workspaceId={workspaceId}
        />
      )}
    </>
  );
}
